export const ErrorCode = {
  INVALID_TYPE: "invalid_type",
  TOO_BIG: "too_big",
  TOO_SMALL: "too_small",
  INVALID_FORMAT: "invalid_format",
  NOT_MULTIPLE_OF: "not_multiple_of",
  UNRECOGNIZED_KEYS: "unrecognized_keys",
  INVALID_UNION: "invalid_union",
  INVALID_KEY: "invalid_key",
  INVALID_ELEMENT: "invalid_element",
  INVALID_VALUE: "invalid_value",
  INVALID_EMAIL_OR_PASSWORD: "INVALID_EMAIL_OR_PASSWORD",
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export type FormErrors = Record<
  string,
  {
    message: string;
    code?: ErrorCode;
  }
>;

export interface AdminTask {
  id: string;
  name: string;
  description: string;
}

export interface AdminClub {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
  features: {
    tournaments: boolean;
    pointsOfSale: boolean;
    users?: boolean;
    players?: boolean;
    openMatches?: boolean;
  };
  members?: Array<AdminClubMember>;
}

export interface AdminStats {
  icon: string;
  label: string;
  value: number;
  description: string;
}

export interface AdminClubMember {
  id: string;
  role: string;
  createdAt?: Date;
  club: {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    email?: string;
    phoneNumber?: string;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture: string | null;
  isSuperAdmin: boolean;
  createdAt: Date;
  updatedAt?: Date;
  members: Array<AdminClubMember>;
}

export interface ApiResponseList<T> {
  results: T[];
  count: number;
}

export interface CreateProduct {
  name: string;
  price: number;
  sku: string;
}

export interface UpdateProduct {
  name?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture: string;
  createdAt: string;
}

export interface ClubManager {
  id: string;
  user: User;
  role: string;
}

export interface Club {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  latitude: string;
  longitude: string;
  phoneNumber: string;
  email: string;
  description: string;
  numberOfIndoorCourts: number;
  numberOfOutdoorCourts: number;
  openingHours: string;
  imageLogo: string;
  ownerId: string;
  nextLoyaltyCardCode: string;
  loyaltyCardEnabled: boolean;
  neededLoyaltyCardAmount: number;
  website: string;
  status: "DRAFT" | "PUBLISHED" | "CANCELLED";
  members: ClubManager[];
  images: {
    id: string;
    url: string;
  }[];
  courtsAmount?: number;
  tournamentsAmount?: number;
  features: {
    tournaments: boolean;
    pointsOfSale: boolean;
    players: boolean;
    users: boolean;
    openMatches: boolean;
  };
  logo?: string;
  courts: Court[];
  role?: string;
}

export type Category = {
  id: string;
  name: string;
  isUserEligible: boolean;
  description: string;
  tagList: string[];
  groupStage?: any;
  eliminationStage?: any;
};

export type OpenMatch = {
  id: string;
  dateTime: string;
  duration: number;
  club: {
    id: string;
    name: string;
    phoneNumber: string;
    imageLogo: string;
  };
  playerOneName: string;
  playerTwoName: string;
  playerThreeName: string;
  playerFourName: string;
  category?: {
    id: string;
    name: string;
    isUserEligible: boolean;
  };
};

export type Registration = {
  id: string;
  name: string;
  playerOne: string;
  playerTwo: string;
  category: Category;
  categoryId: number;
  tournament: Tournament;
  tournamentId: string;
  phone: string;
  confirmed: boolean;
};

export type Match = {
  id: string;
  name: string;
  clubId: string;
  dateTime: string;
  duration: number;
  playerOneName: string;
  playerTwoName: string;
  playerThreeName: string;
  playerFourName: string;
  categoryId: string;
  category: Category;
  winner: Registration | null;
};

export type Tournament = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status:
    | "PENDING"
    | "CONFIGURING_DATETIME_PHASES"
    | "OPEN_INSCRIPTIONS"
    | "ACTIVE"
    | "FINISHED";
  mode: "FULL" | "INSCRIPTION_ONLY";
  clubId: string;
  registrationOpen: boolean;
  categories: Category[];
  images: {
    id: string;
    url: string;
  }[];
  registrations: Registration[];
  club: Club;
};

export type PublicTournament = Tournament & {
  groupPhaseWindows: GroupPhaseWindow[];
  minRequiredInterests: number;
};

export interface CreateRegistration {
  playerOne: string;
  playerTwo: string;
  category: string;
}

export interface CreatePublicRegistration {
  playerOne: string;
  playerTwo: string;
  phone: string;
  category: string;
  timeInterests: string[];
}

export interface GroupPhaseWindow {
  windowId: string;
  start: string;
  end: string;
  availableCount: number;
}

export interface RegistrationConfirmResponse {
  playerOne: string;
  playerTwo: string;
  category: { name: string };
  tournament: { name: string };
}

export interface Reservation {
  id: string;
  title: string;
  start: string;
  end: string;
  tournament?: Tournament;
  phase?: "GROUP_PHASE" | "ELIMINATION_PHASE" | null;
  assigned: boolean;
  type: "TOURNAMENT" | "NORMAL";
}

export type Court = {
  id: string;
  name: string;
  type: "INDOOR" | "OUTDOOR";
  mode: "SINGLE" | "DOUBLE";
};

export interface CreateCourt {
  name: string;
  type: "INDOOR" | "OUTDOOR";
  mode: "SINGLE" | "DOUBLE";
}

// Products
export interface Product {
  id: string;
  name: string;
  sku: string;
  imageUrl: string;
  price: number;
  category?: ProductCategory;
  showOnPOS: boolean;
  sales: {
    id: string;
  }[];
}

export interface ProductCategory {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
}

export interface CreateProductCategory {
  name: string;
  description: string;
}

export interface UpdateProductCategory {
  name?: string;
  description?: string;
}

export interface PointOfSale {
  id: string;
  name: string;
  description: string;
  token: string;
  clubToken: string;
  enablePlayerRegistration: boolean;
}

export interface CreateSaleItem {
  productIds: string[];
  playerId?: string;
  name?: string;
  price?: number;
}

export interface SaleItem {
  id: string;
  name?: string;
  courtId?: string;
  productId: string;
  quantity: number;
  price?: number | null;
  createdAt: string;
  updatedAt: string;
  product: Product;
  court?: Court;
  pointOfSale: PointOfSale;
  player?: Player;
}

export interface Sale {
  id: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  items: SaleItem[];
  player?: Player;
}

export interface Player {
  id: string;
  phone: string;
  name: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  clubPlayers: ClubPlayer[];
}

export interface ClubPlayer {
  id: string;
  clubId: string;
  playerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlayer {
  countryCode?: string;
  name: string;
  lastName: string;
  phone: string;
}

export interface CreateOpenMatch {
  courtId: string;
  start: string; // ISO datetime
  end: string; // ISO datetime
  categoryId?: string;
  playerOneName?: string;
  playerTwoName?: string;
  playerThreeName?: string;
  playerFourName?: string;
}

export interface UpdateOpenMatch {
  courtId?: string;
  start?: string;
  end?: string;
  categoryId?: string | null;
  playerOneName?: string;
  playerTwoName?: string;
  playerThreeName?: string;
  playerFourName?: string;
}

export interface WebOpenMatch {
  id: string;
  dateTime: string;
  duration: number;
  court: {
    id: string;
    name: string;
  };
  category?: {
    id: string;
    name: string;
  };
  playerOneName: string;
  playerTwoName: string;
  playerThreeName: string;
  playerFourName: string;
}
